import type { HttpClient } from '@/infrastructure/http/HttpClient'
import type { RenditionPreviewItemInput, RenditionPreviewResult } from '@/domain/rendition/RenditionRepository'
import { toPreviewBody, mapPreviewResult, type RawPreviewResult } from '@/infrastructure/rendition/renditionPayload'
import type {
  Product,
  RenditionRepository,
  RenditionPolicy,
  RenditionPolicyForm,
  RenditionFormData,
  RenditionPolicyData,
  RenditionBankOptions,
  RenditionDraftDetail,
  CatalogOption,
  CostCenterOption,
  SubcategoryOption,
  AccountOption,
  SplitCustomFieldOption,
  UploadedDocument,
} from '@/domain/rendition/RenditionRepository'
import type Provider from '@/types/provider'
import type Bank from '@/types/bank'
import type { Field } from '@/types/templates'
import { parseSplitFieldVisibility, type RawSplitFieldVisibility } from '@/utils/splitFieldVisibility'
import type { Rendition, RenditionLine, LineTax } from '@/domain/rendition/Rendition'
import {
  buildRenditionFormData,
  hydrateRenditionDraft,
  hydrateInvoiceLinesForRendition,
  type RawRenditionDetail,
  type RawInvoiceDetail,
} from './renditionPayload'

/** Raw shape of the `purchases/invoices/form` bootstrap response (classification catalogs). */
interface InvoiceFormResponse {
  providers?: Provider[]
  categories?: CatalogOption[]
  subcategories?: Array<{ id: number; name: string; category_id?: number }>
  areas?: CatalogOption[]
  currencies?: string[]
}

/** Raw shape of the `purchases/splits/form` response — the canonical accounting reference (same as the platform). */
interface SplitsFormResponse {
  cost_centers?: Array<{ id: number; name: string; code?: string }>
  accounts?: Array<{ id: number; name: string; code?: string }>
  splittable_custom_fields?: Array<{ id: number; name: string; options?: string[]; default_option?: string }>
}

/** Raw shape of the `purchases/policies/{id}/data` response — the `field_visibility` block + the
 *  top-level `mandatory_accounting_data` flag are read here. */
interface PolicyDataResponse {
  default_currency?: string
  mandatory_accounting_data?: boolean
  mandatory_subcategory?: boolean
  mandatory_invoice_description?: boolean
  /** The categories the policy allows. Already narrowed server-side (`policy.categories`). */
  categories?: Array<{ id: number; name: string }>
  field_visibility?: {
    general_description?: boolean
    area_input?: boolean
    provider_info?: boolean
    cost_center?: boolean
    hide_header_subcategory?: boolean
  }
  /** Policy-narrowed cost centers for the request-level selector (same key RequestForm reads). */
  request_cost_centers?: Array<{ id: number; name: string; code?: string }>
  split_field_visibility?: RawSplitFieldVisibility
  /**
   * Withholdings the policy defines. Same key the payment request reads (`RequestForm.vue`), where each
   * entry's `tax_type` is the DISPLAY NAME ("ReteIva", "Retención Honorarios") — not to be confused with
   * the rendition's `LineTax.taxTypeCode`, which is the kind (TAX | WITHHOLDING).
   */
  withholdings_masters?: Array<{
    id?: number
    tax_type?: string | null
    percentage?: number | string | null
  }>
}

/** Raw shape of the `purchases/requests/form` bootstrap — only banks + account types are read here. */
interface RequestFormResponse {
  banks?: Bank[]
  account_types?: string[]
}

/**
 * Raw shape of `categories/{id}/products/?recipient_type=EMPLOYEE` — the beneficiary source (same
 * endpoint the payment-request EXPENSE flow reads). `providers` carries the whole society (not just
 * EMPLOYEE) with bank data in `bank_accounts[]`; `recipient` is the backend-resolved "me" (unused here,
 * the store defaults to me client-side against the list).
 */
interface CategoryProductsEmployeesResponse {
  providers?: Provider[]
  recipient?: Provider | null
}

interface ProductsListResponse {
  count: number,
  next: string | null,
  previous: string | null,
  results: Product[],
  country: string,
}

/**
 * Policy `withholdings_masters` → WITHHOLDING-kind `LineTax` rows used as the seed for every new line.
 *
 * The base is left at 0 on purpose: the line's recompute fills it (line net, or the line's IVA for
 * ReteIva) as soon as a unit price is entered. Unnamed entries are dropped — an unnamed row would reach
 * the payload filter as a blank "0%" withholding. `percentage` can arrive as a string (DRF Decimal).
 */
function mapWithholdingMasters(raw: PolicyDataResponse['withholdings_masters']): LineTax[] {
  return (raw ?? [])
    .filter((w) => !!w.tax_type?.trim())
    .map((w) => ({
      taxName: w.tax_type!.trim(),
      taxPercentage: Number(w.percentage ?? 0) || 0,
      taxableAmount: 0,
      taxAmount: 0,
      taxTypeCode: 'WITHHOLDING' as const,
      fromPolicy: true,
    }))
}

/**
 * The category/products serializer ships bank data only in `bank_accounts[]`, but the rendition
 * beneficiary block reads the flat `bank` / `account_type` / `account_number` (see `setBeneficiary` and
 * `beneficiaryBankMissing`). Mirror the principal NATIONAL account onto those, preferring an already-flat
 * value if the payload ever carries one.
 */
function withFlatPrincipalBank(provider: Provider): Provider {
  const accounts = provider.bank_accounts ?? []
  const principal =
    accounts.find((a) => a.is_principal && a.national_type === 'NATIONAL') ??
    accounts.find((a) => a.national_type === 'NATIONAL')
  if (!principal) return provider
  return {
    ...provider,
    bank: provider.bank ?? principal.bank?.name ?? undefined,
    account_type: provider.account_type ?? principal.account_type ?? undefined,
    account_number: provider.account_number ?? principal.account_number ?? undefined,
  }
}

export class HttpRenditionRepository implements RenditionRepository {
  constructor(private http: HttpClient) {}

  async create(rendition: Rendition, options?: { status?: 'DRAFT'; decimalsFor?: (currency: string) => number }): Promise<{ id: number }> {
    const formData = buildRenditionFormData(rendition, options?.decimalsFor)
    const req = this.http.url('purchases/requests').body(formData)
    if (options?.status) req.params({ status: options.status })
    return req.post<{ id: number }>()
  }

  async getById(id: number, formData: RenditionFormData): Promise<RenditionDraftDetail> {
    const data = await this.http.url(`purchases/requests/${id}`).get<RawRenditionDetail>()
    return hydrateRenditionDraft(data, formData)
  }

  async preview(policyId: number, items: RenditionPreviewItemInput[]): Promise<RenditionPreviewResult> {
    const data = await this.http.url('purchases/requests/rendition-preview').body(toPreviewBody(policyId, items)).post<RawPreviewResult>()
    return mapPreviewResult(data)
  }

  async update(id: number, rendition: Rendition, options?: { status?: 'DRAFT'; decimalsFor?: (currency: string) => number }): Promise<{ id: number }> {
    const formData = buildRenditionFormData(rendition, options?.decimalsFor)
    const req = this.http.url(`purchases/requests/${id}/resubmit`).body(formData)
    if (options?.status) req.params({ status: options.status })
    return req.post<{ id: number }>()
  }

  async listPolicies(): Promise<RenditionPolicy[]> {
    // The request-form bootstrap endpoint returns all policies; filter to RENDITION client-side.
    const data = await this.http.url('purchases/requests/form').get<{ policies: RenditionPolicy[] }>()
    return (data.policies ?? []).filter((p) => p.request_type === 'RENDITION')
  }

  async getPolicyForm(policyId: number): Promise<RenditionPolicyForm> {
    // Same endpoint the payment-request wizard uses: the policy's `data` bundle carries the form.
    const data = await this.http
      .url(`purchases/policies/${policyId}/data`)
      .get<{ form?: { form?: Field[]; name?: string; description?: string } }>()
    return {
      fields: data.form?.form ?? [],
      name: data.form?.name ?? '',
      description: data.form?.description ?? '',
    }
  }

  async getInvoiceLines(invoiceId: number): Promise<RenditionLine[]> {
    // The association selector's listing (purchases/invoices/pending/) carries no breakdown. The lines
    // come from the pending-invoice lines read, which shares the listing's scope: the invoice detail
    // hides invoices outside the user's area and answered 404 to a REQUESTER (Clínica Everest).
    // `hydrateInvoiceLinesForRendition` collapses consolidated groups.
    const data = await this.http.url(`purchases/invoices/pending/${invoiceId}/lines`).get<RawInvoiceDetail>()
    return hydrateInvoiceLinesForRendition(data)
  }

  async loadFormData(): Promise<RenditionFormData> {
    // Classification catalogs come from the invoice-form bootstrap; the accounting reference (cost centers,
    // chart accounts, splittable custom fields) comes from purchases/splits/form — the same canonical
    // endpoint the platform's Splits UIs use. Fetched in parallel.
    const [invoiceForm, splitsForm, productsData] = await Promise.all([
      this.http.url('purchases/invoices/form').get<InvoiceFormResponse>(),
      this.http.url('purchases/splits/form').get<SplitsFormResponse>(),
      this.http.url('purchases/products/?page_size=-1&status=ACTIVE').get<ProductsListResponse>(),
    ])
    const subcategories: SubcategoryOption[] = (invoiceForm.subcategories ?? []).map((s) => ({
      id: s.id,
      name: s.name,
      categoryId: s.category_id,
    }))
    const costCenters: CostCenterOption[] = (splitsForm.cost_centers ?? []).map((c) => ({ id: c.id, name: c.name, code: c.code }))
    const accounts: AccountOption[] = (splitsForm.accounts ?? []).map((a) => ({ id: a.id, name: a.name, code: a.code }))
    const splitCustomFields: SplitCustomFieldOption[] = (splitsForm.splittable_custom_fields ?? []).map((f) => ({
      id: f.id, name: f.name, options: f.options ?? [], defaultOption: f.default_option,
    }))
    return {
      providers: invoiceForm.providers ?? [],
      categories: invoiceForm.categories ?? [],
      subcategories,
      areas: invoiceForm.areas ?? [],
      costCenters,
      accounts,
      splitCustomFields,
      currencies: invoiceForm.currencies ?? [],
      products: productsData.results ?? [],
    }
  }

  async loadPolicyData(policyId: number): Promise<RenditionPolicyData> {
    // Same endpoint the payment-request form uses; renditions read the field_visibility flags, the
    // top-level mandatory_accounting_data flag (mirrors RequestForm's `mandatory_accounting_data`),
    // withholdings_masters (the base withholdings each new line is seeded with) and the categories the
    // policy allows (already narrowed server-side; empty = no restriction).
    const data = await this.http.url(`purchases/policies/${policyId}/data`).get<PolicyDataResponse>()
    const fv = data.field_visibility ?? {}
    return {
      defaultCurrency: data.default_currency,
      fieldVisibility: {
        generalDescription: fv.general_description ?? false,
        areaInput: fv.area_input ?? false,
        providerInfo: fv.provider_info ?? false,
        costCenter: fv.cost_center ?? false,
        hideHeaderSubcategory: fv.hide_header_subcategory ?? false,
        mandatorySubcategory: data.mandatory_subcategory ?? false,
        mandatoryAccounting: data.mandatory_accounting_data ?? false,
        mandatoryInvoiceDescription: data.mandatory_invoice_description ?? false,
      },
      baseWithholdings: mapWithholdingMasters(data.withholdings_masters),
      categoryIds: (data.categories ?? []).map((c) => c.id),
      requestCostCenters: (data.request_cost_centers ?? []).map((c) => ({ id: c.id, name: c.name, code: c.code })),
      splitFieldVisibility: parseSplitFieldVisibility(data.split_field_visibility),
    }
  }

  async loadBankOptions(): Promise<RenditionBankOptions> {
    // The request-form bootstrap carries the country-scoped national bank + account-type catalogs.
    const data = await this.http.url('purchases/requests/form').get<RequestFormResponse>()
    return { banks: data.banks ?? [], accountTypes: data.account_types ?? [] }
  }

  async loadEmployees(categoryId: number): Promise<Provider[]> {
    // Same endpoint the payment-request EXPENSE flow uses: NOT gated by the caller's role, so a REQUESTER
    // (Gabi's case) sees every collaborator, unlike providers/?type_filter=EMPLOYEE which the backend
    // narrows to the ones the user created/was assigned. The category is required by the URL but ignored
    // for EMPLOYEE (the branch reads the whole society), so the list is category-independent.
    const data = await this.http
      .url(`purchases/categories/${categoryId}/products`)
      .params({ recipient_type: 'EMPLOYEE' })
      .get<CategoryProductsEmployeesResponse>()
    // The endpoint does NOT narrow by type for EMPLOYEE — it returns the whole society — so keep only the
    // collaborators, matching the old `type_filter=EMPLOYEE`. A vendor as a reimbursement beneficiary would
    // be a regression.
    return (data.providers ?? [])
      .filter((p) => p.type === 'EMPLOYEE')
      .map(withFlatPrincipalBank)
  }

  async uploadDocument(file: File): Promise<UploadedDocument> {
    const fd = new FormData()
    fd.append('file', file)
    const data = await this.http
      .url('users/rendition-files/upload')
      .body(fd)
      .post<{ id: number; file_url: string; filename: string; extension: string }>()
    return { id: data.id, fileUrl: data.file_url, filename: data.filename, extension: data.extension }
  }
}
